import { RiskEvent } from './riskData';

const RISK_EVENTS_KEY = 'ga_risk_events';
const COMPANY_ANALYSES_KEY = 'ga_company_analyses';
const REPORTS_KEY = 'ga_reports';
const SANCTIONS_CACHE_KEY = 'ga_sanctions_cache';

export interface CompanyAnalysis {
  id: string;
  companyName: string;
  suppliers: string[];
  regions: string[];
  logisticsRoutes: string[];
  dependencies: string[];
  operationalScore: number;
  geopoliticalScore: number;
  sanctionsScore: number;
  cyberScore: number;
  overallRisk: 'Critical' | 'High' | 'Medium' | 'Low';
  summary: string;
  createdAt: string;
  updatedAt: string;
}

export interface GeneratedReport {
  id: string;
  title: string;
  type: string;
  generatedAt: string;
  companyProfile: string;
  companyName: string;
  riskSnapshot: {
    criticalEvents: number;
    highEvents: number;
    escalatingEvents: number;
    sanctionsMatches: number;
    overallRiskScore: number;
  };
  content: {
    executiveSummary: string;
    threatAssessment: string;
    exposureAnalysis: string;
    escalationForecast: string;
    recommendedActions: string;
  };
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
}

export interface SanctionsCache {
  entries: SanctionEntry[];
  lastUpdated: string;
  source: string;
}

export interface SanctionEntry {
  uid: string;
  name: string;
  type: string;
  programs: string[];
  country: string;
  dateAdded: string;
  remarks: string;
}

export function getRiskEvents(): RiskEvent[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(RISK_EVENTS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as RiskEvent[];
  } catch {
    return [];
  }
}

export function saveRiskEvents(events: RiskEvent[]): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(RISK_EVENTS_KEY, JSON.stringify(events));
}

export function addRiskEvent(event: RiskEvent): void {
  const events = getRiskEvents();
  events.unshift(event);
  saveRiskEvents(events);
}

export function updateRiskEvent(id: string, updates: Partial<RiskEvent>): void {
  const events = getRiskEvents();
  const idx = events.findIndex(e => e.id === id);
  if (idx !== -1) {
    events[idx] = { ...events[idx], ...updates };
    saveRiskEvents(events);
  }
}

export function deleteRiskEvent(id: string): void {
  const events = getRiskEvents();
  saveRiskEvents(events.filter(e => e.id !== id));
}

export function getCompanyAnalyses(): CompanyAnalysis[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(COMPANY_ANALYSES_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as CompanyAnalysis[];
  } catch {
    return [];
  }
}

export function saveCompanyAnalysis(analysis: CompanyAnalysis): void {
  const analyses = getCompanyAnalyses();
  const idx = analyses.findIndex(a => a.id === analysis.id);
  if (idx !== -1) {
    analyses[idx] = analysis;
  } else {
    analyses.unshift(analysis);
  }
  localStorage.setItem(COMPANY_ANALYSES_KEY, JSON.stringify(analyses));
}

export function deleteCompanyAnalysis(id: string): void {
  const analyses = getCompanyAnalyses();
  localStorage.setItem(COMPANY_ANALYSES_KEY, JSON.stringify(analyses.filter(a => a.id !== id)));
}

// Report storage functions
export function getReports(): GeneratedReport[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(REPORTS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as GeneratedReport[];
  } catch {
    return [];
  }
}

export function saveReport(report: GeneratedReport): void {
  const reports = getReports();
  const idx = reports.findIndex(r => r.id === report.id);
  if (idx !== -1) {
    reports[idx] = report;
  } else {
    reports.unshift(report);
  }
  localStorage.setItem(REPORTS_KEY, JSON.stringify(reports));
}

export function deleteReport(id: string): void {
  const reports = getReports();
  localStorage.setItem(REPORTS_KEY, JSON.stringify(reports.filter(r => r.id !== id)));
}

// Sanctions cache functions
export function getSanctionsCache(): SanctionsCache | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(SANCTIONS_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SanctionsCache;
  } catch {
    return null;
  }
}

export function saveSanctionsCache(cache: SanctionsCache): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(SANCTIONS_CACHE_KEY, JSON.stringify(cache));
}
